import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import styles from './MostUsedWorkflow.module.scss';
import { useFetch } from 'hooks/useFetch';
import { dashboardEndpoints } from 'hooks/endpoints';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import Modal from 'component/hoc/modal/Modal';
import TableComponent from 'component/molecules/Table/TableComponent';
import NoData from 'component/Custom/NoData/NoData';
import headerData from './factory/HeaderData';

const MESSAGES_COLORS = [
  '#165DFF',
  '#0746A6',
  '#4C89EA',
  '#00B3FF',
  '#0091CF',
  '#76BBF0',
];
const MIN_HEIGHT = 340;

const MostUsedWorkflow = ({
  id,
  startDate,
  endDate,
  snapshotMode,
  botType,
}) => {
  const MAX_HEIGHT = snapshotMode ? 600 : 400;
  const chartRef = useRef(null);
  const [series, setSeries] = useState([{ name: 'Usage Count', data: [] }]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState({});

  const { data, refetch } = useFetch(
    ['topWorkflows', startDate, endDate, id, botType],
    dashboardEndpoints.getTopWorkflows(startDate, endDate, id),
    { enabled: false },
    id ? { botId: id, chatSource: botType } : {}
  );

  const {
    data: questionsByWorkflow,
    refetch: refetchQuestionsByWorkflow,
    isLoading,
  } = useFetch(
    [
      'questions-by-workflow',
      startDate,
      endDate,
      id,
      selectedWorkflow?.workflowId,
      botType,
    ],
    dashboardEndpoints.getQuestionsByWorkflow(startDate, endDate),
    { enabled: false },
    {
      ...(id && { botId: id, chatSource: botType }),
      ...(selectedWorkflow?.workflowId && {
        WorkflowId: selectedWorkflow.workflowId,
      }),
    }
  );

  useEffect(() => {
    if (selectedWorkflow && botType !== undefined) {
      refetchQuestionsByWorkflow();
    }
  }, [selectedWorkflow, botType, id]);

  useEffect(() => {
    if (botType !== undefined) {
      refetch();
    }
  }, [startDate, endDate, botType, id]);

  useEffect(() => {
    const rows = data?.data || [];
    const cats = rows.map((r) => r.workflowName);
    const vals = rows.map((r) => r.nodeCount ?? 0);
    setCategories(cats);
    setSeries([{ name: 'Usage Count', data: vals }]);
  }, [data]);

  const ROW_SLOT = 56;

  const rowCount = useMemo(() => categories?.length || 0, [categories]);

  const chartHeight = useMemo(() => {
    const h = rowCount * ROW_SLOT;
    return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, h));
  }, [rowCount, MAX_HEIGHT]);

  const barHeightPct = useMemo(
    () => (rowCount <= 1 ? '32%' : snapshotMode ? '90%' : '60%'),
    [rowCount, snapshotMode]
  );

  const valueAnnotations = useMemo(() => {
    if (!series[0]?.data?.length) return [];
    const maxValue = Math.max(...series[0]?.data);

    return series[0].data.map((val, i) => ({
      x: val,
      y: categories[i],
      marker: {
        size: 0,
        strokeWidth: 0,
        fillColor: 'transparent',
        strokeColor: 'transparent',
      },
      label: {
        text: String(val),
        style: {
          background: 'transparent',
          color: '#000',
          fontSize: '14px',
          fontWeight: 600,
        },
        borderColor: 'transparent',
        borderWidth: 0,
        offsetX: 16,
        offsetY: rowCount === 1 ? 0 : 12,
      },
    }));
  }, [series, categories, rowCount]);

  const chartSettings = useMemo(
    () => ({
      series,
      options: {
        chart: {
          id: 'messages-by-topic-chart',
          type: 'bar',
          height: chartHeight,
          background: '#FFFFFF',
          toolbar: { show: false },
          animations: { enabled: true },
          // if you want click-to-open like messages by topic,
          // uncomment this and wire to setSelectedWorkflow / setShowModal
          // events: {
          //   dataPointSelection: (_event, _chartContext, config) => {
          //     const idx = config?.dataPointIndex ?? -1;
          //     const rows = data?.data || [];
          //     const selected = rows[idx];
          //     if (selected) {
          //       setSelectedWorkflow(selected);
          //       setShowModal(true);
          //     }
          //   },
          // },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: barHeightPct,
            borderRadius: 8,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'last',
            endingShape: 'rounded',
            distributed: true,
            dataLabels: { position: 'bottom' },
          },
        },
        colors: MESSAGES_COLORS,
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories,
          labels: { show: true },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tickAmount: 5,
        },
        yaxis: {
          show: true,
          labels: {
            show: true,
            maxWidth: 140,
            style: { fontSize: '12px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        grid: {
          show: true,
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { top: 8, right: 80, bottom: 8, left: 20 },
        },
        tooltip: {
          enabled: true,
        },
        legend: { show: false },
        annotations: {
          position: 'front',
          points: valueAnnotations,
        },
      },
    }),
    [series, categories, valueAnnotations, chartHeight]
  );

  const getRows = () => {
    const vals = series?.[0]?.data || [];
    const n = Math.min(categories.length, vals.length);
    const rows = [];
    for (let i = 0; i < n; i += 1) {
      rows.push({ workflow: categories[i] ?? '', usage: Number(vals[i] ?? 0) });
    }
    return rows;
  };

  const downloadCSV = () => {
    const rows = getRows();
    const header = ['Workflow', 'Usage Count'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.join(',')].concat(
      rows.map((r) => `${escape(r.workflow)},${r.usage}`)
    );
    const csv = lines.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'most_used_workflow.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    downloadCSV();
  };

  return (
    <div ref={chartRef} className={styles['chart-container']}>
      <div className={styles['actions-container']}>
        <div className={styles['title-container']}>
          <span
            className={`${styles['title']} ${
              snapshotMode && styles['snapshotMode-title']
            }`}
          >
            Most Used Workflow
          </span>
          {!snapshotMode && (
            <Tooltip
              className={styles['tooltip']}
              content='Shows which workflows were used most often and
how many times during the selected time frame.'
            >
              <SvgWithBranding type='info' color='#682ECB' width={22} />
            </Tooltip>
          )}
        </div>
        {!snapshotMode ? (
          series[0]?.data.length === 0 ? (
            <Tooltip content='No data available to export'>
              <SvgWithBranding
                className={`${styles['download-icon']} ${styles['disabled']}`}
                type='download2'
                onClick={() => {}}
              />
            </Tooltip>
          ) : (
            <SvgWithBranding
              className={`${styles['download-icon']}`}
              type='download2'
              onClick={handleDownload}
            />
          )
        ) : null}
      </div>

      {series[0]?.data?.length === 0 ? (
        <NoData minHeight={470} />
      ) : (
        <ReactApexChart
          options={chartSettings.options}
          series={chartSettings.series}
          type='bar'
          height={chartHeight}
        />
      )}

      <Modal
        title='Application Status'
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentClassName={styles['modal']}
        classname={styles['modal-container']}
        children={
          <TableComponent
            rowKey='id'
            tableContainerClassName={styles['table-container']}
            headerData={headerData}
            externalData={questionsByWorkflow?.data || []}
          />
        }
      />
    </div>
  );
};

export default MostUsedWorkflow;
